/**
 * FieldTripDialog — school-admin self-serve field-trip meals (opened
 * from the "Field trip?" link on /orders/school, and from the day
 * page-level "Field trip?" link).
 *
 * Add to cart stages the dated request for the school checkout. Only checkout
 * posts `POST /api/school-orders/field-trips`:
 *   - daily total ≤ 30 → the server PLACES real bagged-meal orders from
 *     the school's published bagged menu (they appear in the Orders week
 *     view immediately, editable like any other day);
 *   - Any total places orders; over 30/day the Ordo team is ALERTED to
 *     review (notes ride along on the alert). No quantity limit.
 *
 * Mealtime options come from `school.baggedMealtimes` (published bagged
 * menus), quantities are entered PER ORDER COLUMN (the school's
 * configured grouping), and the date picker blocks weekends,
 * deadline-closed days, days outside the selected mealtimes' bagged
 * windows, and cached NO_SERVICE days (server pre-validation backstops
 * days the client can't see).
 *
 * The form collects ONE service date and submits it as a one-day range —
 * `startDate`/`endDate` remain the wire contract.
 *
 * The body is split into two tabs: NEW (the request form) and HISTORY
 * (every upcoming parent-order election, with its "Only option available"
 * read-only Y/N and Edit). Edit loads the row into the New form, where the
 * Y/N and scope can be changed and saved (replaces the election server-side)
 * or the election cancelled (revoked).
 * Sheila, 2026-10-07.
 *
 * `placementId` is minted once per dialog-open and reused across
 * retries, so a partial-failure Retry (or double submit) is idempotent
 * server-side.
 */

import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { cn } from '@ordo/ui/cn'
import { DatePicker } from '@ordo/ui/date-picker'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ordo/ui/dialog'
import { Label } from '@ordo/ui/label'
import { toast } from '@ordo/ui/sonner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { Textarea } from '@ordo/ui/textarea'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, CircleAlert, Ticket } from 'lucide-react'
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { type CreateFieldTripResponse, type FieldTripDayFailure } from '@/api/field-trips'
import {
  type FieldTripElectionScopeKind,
  type FieldTripElectionWire,
  useCreateFieldTripElectionMutation,
  useFieldTripElectionsQuery,
  useRevokeFieldTripElectionMutation,
} from '@/api/field-trips'
import { locationActiveMenusQueryKey, type LocationActiveMenusResponse } from '@/api/orders'
import { type AdminSchool, serviceDateIso } from '@/api/school-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { dateFromKey } from '@/lib/school-orders/calendar'
import { buildMenuOrderColumns } from '@/lib/school-orders/columns'
import { schoolDeadlinePolicy } from '@/lib/school-orders/deadline'
import {
  availableBaggedMealtimes,
  FIELD_TRIP_ALERT_THRESHOLD,
  fieldTripMealKey,
  type FieldTripMealOption,
  fieldTripMealOptions,
  fieldTripSubmitMode,
  isFieldTripDayDisabled,
  localDateKey,
  normalizeSkippedDays,
  planFieldTripQuantities,
  windowsForMealtimes,
} from '@/lib/school-orders/field-trip'
import {
  buildElectionGroupScopes,
  electionFormValid,
  electionScopeLabel,
  electionsForDate,
  electionSubscriptionNote,
  fieldTripElectionSummary,
  type FieldTripMode,
  switchedOrdersSummary,
  toggleGrade,
  upcomingElections,
} from '@/lib/school-orders/field-trip-elections'
import { serviceCheckoutUrl, useSchoolServiceCart } from '@/stores/school-service-cart'

import { FieldTripMealQuantities, GroupQuantityGrid } from './field-trip-meal-quantities'
import { FieldTripMenuPreview, useFieldTripMenuPreview } from './field-trip-menu-preview'

/** Everything the dialog can show besides the request form. */
export type FieldTripResult =
  | { kind: 'result'; result: CreateFieldTripResponse }
  | { kind: 'blocked'; failures: FieldTripDayFailure[] }
  | { kind: 'unavailable'; message: string }

type DialogTab = 'new' | 'history'

type DialogView = { kind: 'form' } | FieldTripResult | { kind: 'elected'; summary: string; note: string }

/** "Tue, Aug 4" for a `YYYY-MM-DD` (ISO datetimes tolerated). */
function dayLabel(dateKeyStr: string): string {
  return dateFromKey(dateKeyStr.slice(0, 10)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function mealtimeLabel(m: string | null): string {
  if (!m) {
    return ''
  }
  return (MEALTIME_LABELS as Record<string, string>)[m] ?? m
}

function ResultHeading({ icon, children }: { icon: ReactNode; children: ReactNode }): ReactElement {
  return (
    <p className="flex items-center gap-2 text-sm font-medium text-foreground">
      {icon}
      {children}
    </p>
  )
}

function DayList({ entries }: { entries: Array<{ key: string; label: string; detail: string | null }> }): ReactElement {
  return (
    <ul className="flex flex-col gap-1.5 rounded-md border bg-muted/30 p-3">
      {entries.map((e) => (
        <li
          key={e.key}
          className="flex flex-col gap-0.5 text-sm sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
          <span className="min-w-0 text-foreground">{e.label}</span>
          {e.detail ? (
            <span className="min-w-0 break-words text-muted-foreground sm:text-right">{e.detail}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/**
 * The post-submit body of the dialog — one block per response variant.
 * Presentational (SSR-testable); the dialog wires Retry / Back.
 */
export function FieldTripResultView({
  view,
  retrying = false,
  onRetry,
  onBack,
}: {
  view: FieldTripResult
  retrying?: boolean
  onRetry?: () => void
  onBack?: () => void
}): ReactElement {
  const okIcon = <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
  const warnIcon = <CircleAlert className="h-4 w-4 shrink-0 text-amber-600" />

  if (view.kind === 'blocked') {
    return (
      <div className="flex flex-col gap-3">
        <ResultHeading icon={warnIcon}>Those days can't be ordered</ResultHeading>
        <DayList
          entries={view.failures.map((f, i) => ({
            key: `${f.date}-${f.mealtime ?? ''}-${i}`,
            label: `${dayLabel(f.date)}${f.mealtime ? ` · ${mealtimeLabel(f.mealtime)}` : ''}`,
            detail: f.message,
          }))}
        />
        <p className="text-sm leading-relaxed text-muted-foreground">
          Nothing was ordered. Adjust the dates or meals and try again.
        </p>
        {onBack ? (
          <div>
            <Button type="button" variant="outline" onClick={onBack}>
              Edit request
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  if (view.kind === 'unavailable') {
    return (
      <div className="flex flex-col gap-3">
        <ResultHeading icon={warnIcon}>Support is temporarily unavailable</ResultHeading>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {view.message || 'We could not reach the Ordo support system.'} Nothing was sent — try again in a moment.
        </p>
        {onRetry ? (
          <div>
            <Button type="button" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Retrying…' : 'Try again'}
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  const result = view.result
  if (result.outcome === 'TICKET_CREATED') {
    return (
      <div className="flex flex-col gap-3">
        <ResultHeading icon={<Ticket className="h-4 w-4 shrink-0 text-foreground" />}>
          Request sent to the Ordo team
        </ResultHeading>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The Ordo team will review and set this up — you'll hear back before the trip. Reference:{' '}
          <span className="font-medium text-foreground">{result.ticketId}</span>
        </p>
      </div>
    )
  }

  const skipped = normalizeSkippedDays(result.skippedDays)
  const alertNote = result.alert ? (
    result.alert.status === 'RAISED' ? (
      <p className="text-xs leading-relaxed text-muted-foreground">
        Flagged for the Ordo team to review (over {FIELD_TRIP_ALERT_THRESHOLD} meals a day). Reference:{' '}
        <span className="font-medium text-foreground">{result.alert.ticketId}</span>
      </p>
    ) : (
      <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-400">
        This order is over {FIELD_TRIP_ALERT_THRESHOLD} meals a day and we could not notify the Ordo team automatically
        — please let your Ordo contact know.
      </p>
    )
  ) : null
  const placedList = (
    <DayList
      entries={result.placements.map((p, i) => ({
        key: `${p.date}-${p.mealtime}-${i}`,
        label: `${dayLabel(p.date)} · ${mealtimeLabel(p.mealtime)}`,
        detail: `${p.quantity} ${p.quantity === 1 ? 'meal' : 'meals'}`,
      }))}
    />
  )
  const skippedNote =
    skipped.length > 0 ? (
      <p className="text-xs leading-relaxed text-muted-foreground">
        Skipped (no service): {skipped.map((s) => dayLabel(s.date)).join(', ')}
      </p>
    ) : null

  if (result.outcome === 'ORDERS_PLACED') {
    return (
      <div className="flex flex-col gap-3">
        <ResultHeading icon={okIcon}>Orders placed</ResultHeading>
        {result.placements.length > 0 ? placedList : null}
        {skippedNote}
        {alertNote}
        <p className="text-sm leading-relaxed text-muted-foreground">
          They're in your Orders view now — edit them like any other day.
        </p>
      </div>
    )
  }

  // ORDERS_PLACED_PARTIAL — placed + failed lists, Retry (same placementId).
  return (
    <div className="flex flex-col gap-3">
      <ResultHeading icon={warnIcon}>Some days couldn't be placed</ResultHeading>
      {result.placements.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Placed</p>
          {placedList}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Not placed</p>
        <DayList
          entries={result.failed.map((f, i) => ({
            key: `${f.date}-${f.mealtime ?? ''}-${i}`,
            label: `${dayLabel(f.date)}${f.mealtime ? ` · ${mealtimeLabel(f.mealtime)}` : ''}`,
            detail: f.message,
          }))}
        />
      </div>
      {skippedNote}
      {alertNote}
      <p className="text-sm leading-relaxed text-muted-foreground">
        Retrying is safe — already-placed days keep their quantities.
      </p>
      {onRetry ? (
        <div>
          <Button type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? 'Retrying…' : 'Retry failed days'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export function FieldTripDialog({
  school,
  open,
  onOpenChange,
}: {
  school: AdminSchool
  open: boolean
  onOpenChange: (open: boolean) => void
}): ReactElement {
  // Band columns, LUNCH-style partition (field trips are bagged lunches;
  // one quantity grid covers every selected mealtime). An untagged menu
  // set spans the full partition; campus coverage intersects — the same
  // columns the order sheet shows, so trip lines land in real columns.
  const columns = buildMenuOrderColumns(school, 'LUNCH', [[]])
  const groupBy = school.orderGroupBy ?? 'AGE_GROUP'
  const columnNoun = groupBy === 'AGE_GROUP' ? 'grade group' : groupBy === 'GRADE' ? 'grade' : 'group'
  const baggedWindows = school.baggedMealtimes
  const mealtimeOptions = useMemo(() => availableBaggedMealtimes(baggedWindows), [baggedWindows])
  const navigate = useNavigate()
  const put = useSchoolServiceCart((state) => state.put)
  const queryClient = useQueryClient()

  const [placementId, setPlacementId] = useState(() => crypto.randomUUID())
  const [view, setView] = useState<DialogView>({ kind: 'form' })
  const [tab, setTab] = useState<DialogTab>('new')
  // The election loaded into the New form by History → Edit, if any.
  const [editing, setEditing] = useState<FieldTripElectionWire | null>(null)
  // ONE service date. The endpoint still takes a start/end range (a trip
  // spanning days is a server-side concept), so a single day submits as a
  // one-day range rather than changing the contract.
  const [serviceDate, setServiceDate] = useState('')
  const [mealtimes, setMealtimes] = useState<ReadonlySet<Mealtime>>(new Set())
  const [qtyByColumn, setQtyByColumn] = useState<Record<string, string>>({})
  // Per-meal inputs, keyed `fieldTripMealKey(mealtime, menuItemId)` → column → quantity,
  // for a mealtime offering more than one meal on the picked date.
  const [qtyByMeal, setQtyByMeal] = useState<Record<string, Record<string, string>>>({})
  const [notes, setNotes] = useState('')
  // "Order for students" (today's flow) vs "Enable parent orders" — a
  // per-date election that opens the field trip meal to parents of the
  // whole school or of selected grades (Sheila, 2026-09-10).
  const canEnableParentOrders = school.parentOrderingEnabled !== false
  const [selectedOrderMode, setOrderMode] = useState<FieldTripMode>('ORDER_FOR_STUDENTS')
  const orderMode = canEnableParentOrders ? selectedOrderMode : 'ORDER_FOR_STUDENTS'
  const [scopeKind, setScopeKind] = useState<FieldTripElectionScopeKind>('ALL')
  const [grades, setGrades] = useState<string[]>([])
  // "Disable all non-field-trip menu items on this day?" — Yes closes every
  // other menu to the in-scope students for the date (Sheila, 2026-10-06).
  const [fieldTripOnly, setFieldTripOnly] = useState(false)
  const gradeOptions = school.gradeOptions ?? []
  const electionsQuery = useFieldTripElectionsQuery(school.id, { enabled: open && canEnableParentOrders })
  const createElection = useCreateFieldTripElectionMutation(school.id)
  const revokeElection = useRevokeFieldTripElectionMutation(school.id)

  // Re-seed the draft (and mint a fresh placementId) each time the dialog
  // opens. LUNCH is preselected when the school has a bagged lunch menu.
  useEffect(() => {
    if (!open) {
      return
    }
    const available = availableBaggedMealtimes(school.baggedMealtimes)
    const initial = available.includes('LUNCH') ? ['LUNCH' as Mealtime] : available.slice(0, 1)
    setPlacementId(crypto.randomUUID())
    setView({ kind: 'form' })
    setTab('new')
    setEditing(null)
    setServiceDate('')
    setMealtimes(new Set(initial))
    setQtyByColumn({})
    setQtyByMeal({})
    setNotes('')
    setOrderMode('ORDER_FOR_STUDENTS')
    setScopeKind('ALL')
    setGrades([])
  }, [open, school.id, school.baggedMealtimes, canEnableParentOrders])

  function toggleMealtime(mealtime: Mealtime, on: boolean): void {
    setMealtimes((prev) => {
      const next = new Set(prev)
      if (on) {
        next.add(mealtime)
      } else {
        next.delete(mealtime)
      }
      return next
    })
  }

  // NO_SERVICE probe — cached day contexts only (the week view fetches
  // them per day with this exact key). Uncached days stay pickable; the
  // server pre-validation is the backstop and reports them as blocked.
  const isNoService = useCallback(
    (dayKey: string): boolean => {
      const cached = queryClient.getQueryData<LocationActiveMenusResponse>(
        locationActiveMenusQueryKey({ locationId: school.id, activeOn: serviceDateIso(dayKey), withAllergens: true }),
      )
      return cached?.dayContext?.noServiceDay != null
    },
    [queryClient, school.id],
  )

  const selectedWindows = useMemo(() => windowsForMealtimes(baggedWindows, [...mealtimes]), [baggedWindows, mealtimes])
  const disabledDates = useCallback(
    (date: Date) =>
      isFieldTripDayDisabled(localDateKey(date), {
        policy: schoolDeadlinePolicy(school),
        windows: selectedWindows,
        isNoService,
      }),
    [school, selectedWindows, isNoService],
  )

  // The same query the menu preview shows: a mealtime offering more than one
  // meal that day takes a quantity per meal instead of one shared quantity.
  const preview = useFieldTripMenuPreview({
    locationId: school.id,
    serviceDate,
    mealtimes: [...mealtimes],
    enabled: open,
  })
  const optionsByMealtime = useMemo(() => {
    const menus = preview.data?.menusForLocation ?? []
    const out: Partial<Record<Mealtime, FieldTripMealOption[]>> = {}
    for (const mealtime of mealtimes) {
      out[mealtime] = fieldTripMealOptions(menus, mealtime)
    }
    return out
  }, [preview.data, mealtimes])
  const quantities = useMemo(
    () => planFieldTripQuantities({ mealtimes: [...mealtimes], optionsByMealtime, columns, qtyByColumn, qtyByMeal }),
    [mealtimes, optionsByMealtime, columns, qtyByColumn, qtyByMeal],
  )
  const dailyTotal = quantities.dailyTotal
  const mode = fieldTripSubmitMode(dailyTotal)
  const sortedMealtimes = sortMealtimes(mealtimes)
  const showMealtimeHeadings = sortedMealtimes.length > 1

  function setMealQuantity(mealtime: Mealtime, menuItemId: string, columnId: string, value: string): void {
    const key = fieldTripMealKey(mealtime, menuItemId)
    setQtyByMeal((prev) => ({ ...prev, [key]: { ...prev[key], [columnId]: value } }))
  }

  const existingElections = useMemo(
    () => electionsForDate(electionsQuery.data ?? [], serviceDate),
    [electionsQuery.data, serviceDate],
  )
  // History / edit: every upcoming election at the campus, whatever date is
  // picked, so an admin can see what is already enabled and change it here.
  const enabledElections = useMemo(
    () => upcomingElections(electionsQuery.data ?? [], localDateKey(new Date())),
    [electionsQuery.data],
  )
  const valid =
    orderMode === 'ENABLE_PARENT_ORDERS'
      ? electionFormValid({ serviceDate, mealtimes, scopeKind, grades })
      : /^\d{4}-\d{2}-\d{2}$/.test(serviceDate) && mealtimes.size > 0 && quantities.complete
  const submitting = createElection.isPending

  /** History → Edit: load an election into the New form. */
  function startEdit(election: FieldTripElectionWire): void {
    if (!canEnableParentOrders) {
      return
    }
    setEditing(election)
    setTab('new')
    setView({ kind: 'form' })
    setOrderMode('ENABLE_PARENT_ORDERS')
    setServiceDate(election.serviceDate)
    setMealtimes(new Set([election.mealtime as Mealtime]))
    setScopeKind(election.scopeKind)
    setGrades(election.groupScopes.filter((g) => g.dimension === 'GRADE').map((g) => g.value))
    setFieldTripOnly(election.fieldTripOnly)
    setNotes(election.notes ?? '')
  }

  function stopEdit(): void {
    setEditing(null)
    setServiceDate('')
    setGrades([])
    setScopeKind('ALL')
    setFieldTripOnly(false)
    setNotes('')
  }

  /** Edit → "Cancel parent ordering": revoke the election being edited. */
  async function handleCancelElection(): Promise<void> {
    if (!editing) {
      return
    }
    try {
      await revokeElection.mutateAsync(editing.id)
      toast.success(`Parent ordering cancelled for ${dayLabel(editing.serviceDate)}`)
      stopEdit()
      setTab('history')
    } catch (e) {
      toast.error('Could not cancel parent ordering', {
        description: e instanceof Error ? e.message : 'Please try again.',
      })
    }
  }

  async function handleEnableParentOrders(): Promise<void> {
    if (!canEnableParentOrders) {
      return
    }
    try {
      // One election per selected mealtime; each replaces that slot's
      // previous election server-side.
      // Editing onto a different day or meal leaves the original slot
      // untouched server-side, so revoke it explicitly.
      if (editing && (editing.serviceDate !== serviceDate || !mealtimes.has(editing.mealtime as Mealtime))) {
        await revokeElection.mutateAsync(editing.id)
      }
      const switchResults = []
      for (const mealtime of sortMealtimes(mealtimes)) {
        const saved = await createElection.mutateAsync({
          locationId: school.id,
          serviceDate,
          mealtime,
          scopeKind,
          groupScopes: scopeKind === 'GROUPS' ? buildElectionGroupScopes(grades) : undefined,
          fieldTripOnly,
          notes: notes.trim() || null,
        })
        switchResults.push(saved.switchedOrders)
      }
      const switchSummary = switchedOrdersSummary(switchResults)
      if (switchSummary) {
        toast.info(switchSummary.title, switchSummary.description ? { description: switchSummary.description } : {})
      }
      setEditing(null)
      setView({
        kind: 'elected',
        summary: fieldTripElectionSummary({ scopeKind, grades, dayLabel: dayLabel(serviceDate) }),
        note: electionSubscriptionNote(scopeKind, fieldTripOnly),
      })
    } catch (e) {
      toast.error('Could not enable parent orders', {
        description: e instanceof Error ? e.message : 'Please try again.',
      })
    }
  }

  async function handleSubmit(): Promise<void> {
    if (orderMode === 'ENABLE_PARENT_ORDERS') {
      await handleEnableParentOrders()
      return
    }
    const input = {
      placementId,
      locationId: school.id,
      startDate: serviceDate,
      endDate: serviceDate,
      mealtimes: sortMealtimes(mealtimes),
      groupQuantities: quantities.groupQuantities,
      ...(quantities.mealSplits.length > 0 ? { mealSplits: quantities.mealSplits } : {}),
      notes: notes.trim() || undefined,
    }
    const reviewLines = sortMealtimes(mealtimes).flatMap((mealtime) => {
      const items = (preview.data?.menusForLocation ?? [])
        .filter((menu) => menu.mealtime === mealtime)
        .flatMap((menu) => menu.items)
      const splits = quantities.mealSplits.filter((split) => split.mealtime === mealtime)
      const chosen = splits.length
        ? splits
        : [{ menuItemId: optionsByMealtime[mealtime]?.[0]?.menuItemId, groupQuantities: quantities.groupQuantities }]
      return chosen.map((split) => {
        const item = items.find((candidate) => candidate.id === split.menuItemId)
        return {
          name:
            optionsByMealtime[mealtime]?.find((option) => option.menuItemId === split.menuItemId)?.title ??
            'Field-trip meal',
          quantity: split.groupQuantities.reduce((sum, group) => sum + group.quantity, 0),
          unitPriceCents: item?.isPriced === false ? null : (item?.customerPriceCents ?? null),
          mealtime,
          detail: `${serviceDate} · ${MEALTIME_LABELS[mealtime]}`,
        }
      })
    })
    put({
      id: placementId,
      kind: 'field-trip',
      locationId: school.id,
      locationName: school.name,
      title: 'Field-trip meals',
      input,
      lines: reviewLines,
    })
    onOpenChange(false)
    navigate(serviceCheckoutUrl(school.id))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md lg:max-w-2xl">
        {view.kind === 'form' ? (
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as DialogTab)}
            className="flex min-h-0 flex-1 flex-col">
            <DialogHeader>
              <DialogTitle>Field-trip meals</DialogTitle>
              <DialogDescription className="leading-relaxed">
                <span className="block font-medium text-foreground" data-testid="field-trip-campus">
                  {school.name}
                </span>
                {tab === 'history'
                  ? 'Every upcoming day parents can order the field trip meal.'
                  : editing
                    ? `Editing parent ordering for ${dayLabel(editing.serviceDate)} — change who can order, or cancel it.`
                    : orderMode === 'ENABLE_PARENT_ORDERS'
                      ? 'Let parents order the field trip meal themselves — pick the day, the meals, and who is going.'
                      : `Order bagged meals for the trip — pick the day, the meals, and how many from each ${columnNoun}.`}
              </DialogDescription>
              {canEnableParentOrders ? (
                <TabsList className="mt-3 grid w-full grid-cols-2">
                  <TabsTrigger value="new">New</TabsTrigger>
                  <TabsTrigger value="history">
                    History
                    {enabledElections.length > 0 ? (
                      <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                        {enabledElections.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                </TabsList>
              ) : null}
            </DialogHeader>

            {canEnableParentOrders ? (
              <TabsContent value="history" className="mt-0 flex min-h-0 flex-1 flex-col">
                <DialogBody>
                  {enabledElections.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Parent ordering is not enabled for any upcoming day.
                    </p>
                  ) : (
                    <Table className="min-w-0">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="px-2">Day</TableHead>
                          <TableHead className="px-2">Meal</TableHead>
                          <TableHead className="px-2">Who</TableHead>
                          <TableHead className="px-2 text-center">Only Option</TableHead>
                          <TableHead className="sr-only">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enabledElections.map((election) => {
                          const day = dayLabel(election.serviceDate)
                          return (
                            <TableRow key={election.id}>
                              <TableCell className="px-2 font-medium">{day}</TableCell>
                              <TableCell className="px-2">{mealtimeLabel(election.mealtime)}</TableCell>
                              <TableCell className="whitespace-normal px-2">
                                {electionScopeLabel({ ...election, fieldTripOnly: false })}
                              </TableCell>
                              <TableCell
                                className="px-2 text-center font-medium tabular-nums"
                                aria-label={`Only option on ${day}: ${election.fieldTripOnly ? 'yes' : 'no'}`}>
                                {election.fieldTripOnly ? 'Y' : 'N'}
                              </TableCell>
                              <TableCell className="px-2 text-right">
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:underline"
                                  aria-label={`Edit parent ordering for ${day}`}
                                  onClick={() => startEdit(election)}>
                                  Edit
                                </button>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  )}
                </DialogBody>
              </TabsContent>
            ) : null}

            <TabsContent value="new" className="mt-0 flex min-h-0 flex-1 flex-col">
              <form
                className="flex min-h-0 flex-1 flex-col"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (valid && !submitting) {
                    void handleSubmit()
                  }
                }}>
                <DialogBody className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <Label>Service date</Label>
                    <DatePicker
                      value={serviceDate}
                      placeholder="Pick a day"
                      className="h-9 w-full"
                      disabledDates={disabledDates}
                      onChange={setServiceDate}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Meals while out</Label>
                    <div className="flex flex-wrap gap-x-5 gap-y-2.5">
                      {mealtimeOptions.map((option) => (
                        <label key={option} className="flex items-center gap-2 text-sm text-foreground">
                          <Checkbox
                            checked={mealtimes.has(option)}
                            onCheckedChange={(c) => toggleMealtime(option, c === true)}
                          />
                          {MEALTIME_LABELS[option]}
                        </label>
                      ))}
                    </div>
                  </div>

                  <FieldTripMenuPreview
                    locationId={school.id}
                    serviceDate={serviceDate}
                    mealtimes={[...mealtimes]}
                    enabled={open}
                  />

                  {canEnableParentOrders ? (
                    <div className="flex flex-col gap-2">
                      <Label>How to order</Label>
                      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="How to order">
                        {(
                          [
                            ['ORDER_FOR_STUDENTS', 'Order for students'],
                            ['ENABLE_PARENT_ORDERS', 'Enable parent orders'],
                          ] as const
                        ).map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            role="radio"
                            aria-checked={orderMode === value}
                            onClick={() => setOrderMode(value)}
                            className={cn(
                              'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
                              orderMode === value
                                ? 'border-transparent bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:bg-muted/50',
                            )}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {canEnableParentOrders && existingElections.size > 0 && !editing ? (
                    <p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                      Parent ordering is already enabled for this day (
                      {[...existingElections.values()].map((e) => mealtimeLabel(e.mealtime)).join(', ')}). Enabling
                      again replaces it;{' '}
                      <button type="button" className="text-primary hover:underline" onClick={() => setTab('history')}>
                        manage it in History
                      </button>
                      .
                    </p>
                  ) : null}

                  {orderMode === 'ENABLE_PARENT_ORDERS' ? (
                    <div className="flex flex-col gap-3">
                      <Label>Who can order</Label>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="election-scope"
                            checked={scopeKind === 'ALL'}
                            onChange={() => setScopeKind('ALL')}
                          />
                          Whole school
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="election-scope"
                            checked={scopeKind === 'GROUPS'}
                            onChange={() => setScopeKind('GROUPS')}
                            disabled={gradeOptions.length === 0}
                          />
                          Selected grades
                          {gradeOptions.length === 0 ? (
                            <span className="text-xs text-muted-foreground">(no grades on the roster yet)</span>
                          ) : null}
                        </label>
                      </div>
                      {scopeKind === 'GROUPS' ? (
                        <div className="flex flex-wrap gap-2" aria-label="Grades">
                          {gradeOptions.map((grade) => (
                            <label
                              key={grade}
                              className={cn(
                                'flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
                                grades.includes(grade)
                                  ? 'border-transparent bg-primary text-primary-foreground'
                                  : 'border-border bg-background',
                              )}>
                              <Checkbox
                                className="sr-only"
                                checked={grades.includes(grade)}
                                onCheckedChange={() => setGrades((prev) => toggleGrade(prev, grade, gradeOptions))}
                              />
                              {grade}
                            </label>
                          ))}
                        </div>
                      ) : null}
                      <Label id="field-trip-only-label">Disable all non-field-trip menu items on this day?</Label>
                      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-labelledby="field-trip-only-label">
                        {(
                          [
                            [true, 'Yes'],
                            [false, 'No'],
                          ] as const
                        ).map(([value, label]) => (
                          <button
                            key={label}
                            type="button"
                            role="radio"
                            aria-checked={fieldTripOnly === value}
                            onClick={() => setFieldTripOnly(value)}
                            className={cn(
                              'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
                              fieldTripOnly === value
                                ? 'border-transparent bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:bg-muted/50',
                            )}>
                            {label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {fieldTripOnly
                          ? 'Yes — the selected students can order only the field trip meal that day; every other menu item is hidden for them.'
                          : 'No — the field trip meal is offered beside the regular menu.'}
                      </p>
                      {valid ? (
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {fieldTripElectionSummary({ scopeKind, grades, dayLabel: dayLabel(serviceDate) })}{' '}
                          {electionSubscriptionNote(scopeKind, fieldTripOnly)}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Label>{quantities.splitMealtimes.length > 0 ? 'How many of each meal' : "Who's going"}</Label>
                      {quantities.splitMealtimes.length === 0 ? (
                        <GroupQuantityGrid
                          heading={null}
                          columns={columns}
                          quantityFor={(columnId) => qtyByColumn[columnId] ?? ''}
                          onQuantityChange={(columnId, value) =>
                            setQtyByColumn((prev) => ({ ...prev, [columnId]: value }))
                          }
                          total={dailyTotal}
                        />
                      ) : (
                        <div className="flex flex-col gap-4">
                          {sortedMealtimes.map((mealtime) => {
                            const heading = showMealtimeHeadings ? MEALTIME_LABELS[mealtime] : null
                            const total = quantities.totals[mealtime] ?? 0
                            // A mealtime with one meal keeps the per-group grid.
                            return quantities.splitMealtimes.includes(mealtime) ? (
                              <FieldTripMealQuantities
                                key={mealtime}
                                heading={heading}
                                options={optionsByMealtime[mealtime] ?? []}
                                columns={columns}
                                quantityFor={(menuItemId, columnId) =>
                                  qtyByMeal[fieldTripMealKey(mealtime, menuItemId)]?.[columnId] ?? ''
                                }
                                onQuantityChange={(menuItemId, columnId, value) =>
                                  setMealQuantity(mealtime, menuItemId, columnId, value)
                                }
                                total={total}
                              />
                            ) : (
                              <GroupQuantityGrid
                                key={mealtime}
                                heading={heading}
                                columns={columns}
                                quantityFor={(columnId) => qtyByColumn[columnId] ?? ''}
                                onQuantityChange={(columnId, value) =>
                                  setQtyByColumn((prev) => ({ ...prev, [columnId]: value }))
                                }
                                total={total}
                              />
                            )
                          })}
                        </div>
                      )}
                      {dailyTotal > 0 ? (
                        <p className="text-xs leading-relaxed text-muted-foreground">{mode.helper}</p>
                      ) : null}
                    </div>
                  )}

                  {orderMode === 'ORDER_FOR_STUDENTS' && mode.showNotes ? (
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="field-trip-notes">Notes for the Ordo team</Label>
                      <Textarea
                        id="field-trip-notes"
                        rows={2}
                        placeholder="Optional — destination, timing, anything unusual"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>
                  ) : null}
                </DialogBody>

                <DialogFooter>
                  {editing ? (
                    <>
                      <Button
                        type="button"
                        variant="destructive"
                        disabled={submitting || revokeElection.isPending}
                        onClick={() => void handleCancelElection()}>
                        {revokeElection.isPending ? 'Cancelling…' : 'Cancel parent ordering'}
                      </Button>
                      <Button type="button" variant="outline" disabled={submitting} onClick={stopEdit}>
                        Discard
                      </Button>
                    </>
                  ) : null}
                  <Button type="submit" disabled={!valid || submitting}>
                    {orderMode === 'ENABLE_PARENT_ORDERS'
                      ? createElection.isPending
                        ? editing
                          ? 'Saving…'
                          : 'Enabling…'
                        : editing
                          ? 'Save changes'
                          : 'Enable parent orders'
                      : 'Add to cart'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Field-trip meals</DialogTitle>
              <DialogDescription>{school.name}</DialogDescription>
            </DialogHeader>
            <DialogBody>
              {view.kind === 'elected' ? (
                <div className="flex flex-col gap-3">
                  <ResultHeading icon={<CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}>
                    Parent ordering enabled
                  </ResultHeading>
                  <p className="text-sm text-foreground">{view.summary}</p>
                  <p className="text-sm text-muted-foreground">{view.note}</p>
                  <div>
                    <Button type="button" variant="outline" onClick={() => setView({ kind: 'form' })}>
                      Back
                    </Button>
                  </div>
                </div>
              ) : (
                <FieldTripResultView
                  view={view}
                  retrying={false}
                  onRetry={() => {
                    if (!submitting) {
                      void handleSubmit()
                    }
                  }}
                  onBack={() => setView({ kind: 'form' })}
                />
              )}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
